/**
 * E002-T034: ExecutionPanel Component
 *
 * Displays execution controls and progress:
 * - Test Run / Execute buttons
 * - Pause / Resume / Stop controls
 * - Progress bar with statistics
 * - Execution results summary
 *
 * E002-T040: E2E Integration
 * - Persists completed logs to Block-Model
 * - Updates lastRunAt/lastRunStatus in Block-Model
 * - Creates InputStageLog from processed rows
 * - Calls workflow engine webhook
 */

import { Button, notify, Tooltip } from '@affine/component';
import { AnimatedRegion } from '@affine/core/components/animation/animated-region';
import { DisclosureChevron } from '@affine/core/components/animation/disclosure-chevron';
import { useUiOfflineSignal } from '@affine/core/components/hooks/use-connectivity-status';
import { useI18n } from '@affine/i18n';
import type { BlockModel } from '@blocksuite/affine/store';
import { streamLainAnimationController } from '@blocksuite/affine-shared/utils';
import { CloseIcon, PlayIcon } from '@blocksuite/icons/rc';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import clsx from 'clsx';
import type { FC } from 'react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { workspaceApiFetch } from '../../../cloud';
import { WorkspaceDialogService } from '../../../dialogs';
import { DocsService } from '../../../doc';
import { EditorService } from '../../../editor';
import { WorkspaceService } from '../../../workspace';
import { isAiKeyError } from '../../hooks/is-ai-key-error';
import { extractConfig } from '../../hooks/use-block-model-config';
import type { AutomationConfig } from '../../services/automation';
import {
  AutomationConfigApiService,
  isVerifySyncBackendUnavailable,
  type VerifySyncConfig,
} from '../../services/automation-config-api';
import {
  DatabaseDiscoveryService,
  type DatabaseInfo,
} from '../../services/database-discovery';
import type { ProcessedRows } from '../../services/database-rows';
import {
  ExecutionService,
  type ExecutionState,
  type ExecutionStatus,
  type ExecutionWarning,
} from '../../services/execution';
import {
  LoggingService,
  type ProcessStageRowEntry,
} from '../../services/logging';
import { type WorkflowInfo, WorkflowService } from '../../services/workflow';
import { WorkflowClientService } from '../../services/workflow-client';
import { WorkflowTestContextService } from '../../services/workflow-test-context';
import { workflowWorkspaceRoutes } from '../../services/workflow-workspace-routes';
import { formatWorkflowErrorMessage } from '../../utils/workflow-error-message';
import {
  fetchWorkflowLimits,
  type WorkflowLimits,
} from '../../utils/workflow-limits';
import { hasConfiguredAutomationInput } from '../automation-input-requirements';
import * as styles from '../automation-panel.css';
import * as startStyles from '../automation-start-page.css';
import { ChooseRowsDialog } from './choose-rows-dialog';
import * as runStyles from './execution-panel.css';
import { ExecutionRowList } from './execution-row-list';
// =============================================================================
// E029-PREFLIGHT: Pre-Flight Types & Comparison (extracted for testability)
// =============================================================================
import {
  buildPreFlightAttributes,
  flushPreFlightDocs,
  getPreFlightDisplayStatus,
  getStaleWorkflowMappingIds,
  type PreFlightAttribute,
  type PreFlightProfile,
  type PreFlightResult,
  type PreFlightStep,
} from './preflight-comparison';
import {
  preFlightDatabaseDirectories,
  preFlightLocalDocs,
} from './preflight-local-docs';
import type { RunRequirement } from './run-requirements-dialog';
import { RunRequirementsDialog } from './run-requirements-dialog';
import { TestDataDialog } from './test-data-dialog';
import { WarningRow } from './warning-row';

interface ExecutionPanelProps {
  /**
   * `footer`: Run, progress and results for the
   * sidebar footer. `test`: "Test with real rows" for the settings panel.
   * Both share the execution and preflight handlers.
   */
  layout: 'footer' | 'test';
  /** The automation is not ready to run; Run stays disabled. */
  runLocked?: boolean;
  /** Shown on the locked Run button; clicking it calls `onRunLockedClick`. */
  runLockedHint?: string;
  onRunLockedClick?: () => void;
  /** Called after a test context was created (e.g. to open the builder). */
  onTestCreated?: () => void;
  /** Block ID for this execution */
  blockId: string;
  /** Whether the automation is configured and ready to run */
  isConfigured: boolean;
  /** Rows to execute (from source table with filters applied) */
  rows?: Array<Record<string, unknown>>;
  /** Processed rows data for logging */
  processedRows?: ProcessedRows | null;
  /** Selected workflow */
  workflow?: WorkflowInfo | null;
  /** Automation config */
  config?: AutomationConfig | null;
  /** Database discovery data used to flush sharded storage root docs. */
  accessibleDatabases?: DatabaseInfo[];
  /** sl-3yai.7.2: Whether compliance guard allows full execution */
  canExecute?: boolean;
  /**
   * A manual run over a table: Run counts the rows chosen in the table and
   * runs exactly those; without a chosen row it explains how to choose.
   */
  chosenRows?: {
    rowIds: string[];
    /** Opens the source table when it lives in another document. */
    onOpenTable?: () => void;
  } | null;
}

// =============================================================================
// Hook to get BlockModel
// =============================================================================

function useAutomationBlockModel(blockId: string | null): BlockModel | null {
  const editorService = useServiceOptional(EditorService);
  const editor = editorService?.editor;
  const editorContainer = useLiveData(editor?.editorContainer$ ?? null);

  return useMemo(() => {
    if (!blockId || !editorContainer) return null;
    const doc = editorContainer.doc;
    return doc?.getBlock(blockId)?.model ?? null;
  }, [blockId, editorContainer]);
}

// =============================================================================
// Sub-Components
// =============================================================================

import type { BadgeStatus } from '../../hooks';

/**
 * E018-S007-T001: StatusBadge using BadgeStatus from useAutomationUIState
 * Displays the unified UI status badge
 */
export const StatusBadge: FC<{ status: BadgeStatus }> = ({ status }) => {
  const t = useI18n();

  const tones: Record<BadgeStatus, runStyles.ExecutionTone> = {
    ready: 'success',
    running: 'active',
    queued: 'warning',
    paused: 'warning',
    completed: 'success',
    cancelled: 'neutral',
    failed: 'error',
  };

  const i18nStatusKey: Record<BadgeStatus, string> = {
    ready: 'com.streamlain.streamlain.execution.status.ready',
    running: 'com.streamlain.streamlain.execution.status.running',
    queued: 'com.streamlain.streamlain.history.queued',
    paused: 'com.streamlain.streamlain.execution.status.paused',
    completed: 'com.streamlain.streamlain.execution.status.completed',
    failed: 'com.streamlain.streamlain.execution.status.failed',
    cancelled: 'com.streamlain.streamlain.history.status-cancelled',
  };

  const i18nTooltipKey: Partial<Record<BadgeStatus, string>> = {
    ready: 'com.streamlain.streamlain.execution.tooltip.ready',
    failed: 'com.streamlain.streamlain.execution.tooltip.failed',
  };

  const tone = tones[status];
  const tooltipKey = i18nTooltipKey[status];

  return (
    <span
      data-testid="automation-execution-status"
      data-status={status}
      className={clsx(runStyles.statusPill, runStyles.toneText[tone])}
      title={tooltipKey ? t[tooltipKey as keyof typeof t]() : undefined}
    >
      <span className={runStyles.dot} aria-hidden="true" />
      {t[i18nStatusKey[status] as keyof typeof t]()}
    </span>
  );
};

// =============================================================================
// E029-PREFLIGHT: Progress Bar with gradient blending + shimmer animation
// =============================================================================

type ProgressPhase = 'preflight' | 'execution';

/** Resolve a step to a gradient colour; pending steps show the track. */
function stepColor(step: PreFlightStep | undefined): string {
  if (!step || step.status === 'pending') return 'transparent';
  if (step.status === 'failed') return runStyles.executionToneColor.error;
  if (step.status === 'done') return runStyles.executionToneColor.success;
  return runStyles.executionToneColor.active; // running
}

// sl-3nkm.8.4: TimeoutState + pure math live in ./timeout-state so unit
// tests can import them without pulling in the lit-loaded component tree.
import { computeTimeoutState, type TimeoutState } from './timeout-state';

const ProgressBar: FC<{
  percentage: number;
  status: ExecutionStatus;
  phase: ProgressPhase;
  preFlightResult: PreFlightResult | null;
  timeoutState?: TimeoutState;
}> = ({ percentage, status, phase, preFlightResult, timeoutState }) => {
  const fillRef = useRef<HTMLDivElement>(null);
  const shimmerRef = useRef<HTMLDivElement>(null);
  const previousFill = useRef(0);
  const isActive = status === 'running';
  const indeterminate = isActive && percentage === 0;
  const fill =
    Math.max(0, Math.min(100, indeterminate ? 100 : percentage)) / 100;
  useLayoutEffect(() => {
    const element = fillRef.current;
    const previous = previousFill.current;
    previousFill.current = fill;
    if (!element) return;
    streamLainAnimationController.play(
      element,
      [{ transform: `scaleX(${previous})` }, { transform: `scaleX(${fill})` }],
      'resize'
    );
    return () => streamLainAnimationController.cancel(element);
  }, [fill, phase]);
  useLayoutEffect(() => {
    const element = shimmerRef.current;
    if (!element || !isActive) return;
    streamLainAnimationController.playLoop(
      element,
      [{ backgroundPosition: '-200% 0' }, { backgroundPosition: '200% 0' }],
      1800
    );
    return () => streamLainAnimationController.cancel(element);
  }, [isActive, phase]);

  // --- Preflight: gradient bar that blends adjacent step colors ---
  if (phase === 'preflight' && preFlightResult) {
    const steps = preFlightResult.steps;
    const colors = steps.map(s => stepColor(s));
    const allDone = steps.every(s => s.status === 'done');
    const anyFailed = steps.some(s => s.status === 'failed');

    // Build a linear-gradient with smooth stops between segments
    // Each segment gets ~25% of the bar, with 2% blend zones
    const stops: string[] = [];
    const segWidth = 100 / colors.length;
    for (let i = 0; i < colors.length; i++) {
      if (i === 0) {
        stops.push(`${colors[i]} 0%`);
        stops.push(`${colors[i]} ${segWidth - 1}%`);
      } else if (i === colors.length - 1) {
        stops.push(`${colors[i]} ${segWidth * i + 1}%`);
        stops.push(`${colors[i]} 100%`);
      } else {
        stops.push(`${colors[i]} ${segWidth * i + 1}%`);
        stops.push(`${colors[i]} ${segWidth * (i + 1) - 1}%`);
      }
    }

    // Overlay: solid green when all steps passed, solid red on failure.
    const overlayTone: runStyles.ExecutionTone = anyFailed
      ? 'error'
      : 'success';

    return (
      <div className={runStyles.bar}>
        <div
          className={runStyles.barLayer}
          style={{ background: `linear-gradient(90deg, ${stops.join(', ')})` }}
        />
        <div
          className={clsx(
            runStyles.barOverlay,
            runStyles.barOverlayTone[overlayTone]
          )}
          data-visible={allDone || anyFailed}
        />
      </div>
    );
  }

  // --- Execution phase: fill bar with shimmer when running ---
  const barTone = ((): runStyles.ExecutionTone => {
    // sl-3nkm.8.4: Timeout warning/error overrides the running/idle
    // colour so the user sees the pressure before the backend flips
    // the status to 'failed'. Terminal statuses keep their own
    // colour — once the run finished, the timeout window is moot.
    if (
      status === 'running' &&
      (timeoutState === 'warning' || timeoutState === 'error')
    ) {
      return timeoutState === 'error' ? 'error' : 'warning';
    }
    switch (status) {
      case 'running':
        return 'active';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      default:
        return 'neutral';
    }
  })();

  return (
    <div className={runStyles.bar}>
      <div
        ref={fillRef}
        className={clsx(runStyles.barFill, runStyles.barFillTone[barTone])}
        data-indeterminate={indeterminate}
        style={{ transform: `scaleX(${fill})` }}
      />
      {isActive && (
        <div
          ref={shimmerRef}
          className={runStyles.barShimmer}
          style={{ width: `${fill * 100}%` }}
        />
      )}
    </div>
  );
};

/**
 * sl-3nkm.8.4: Wraps `ProgressBar` with a warning/error tooltip that
 * surfaces the remaining time before the step hits `maxStepDurationMs`.
 * The tooltip appears only when the run is approaching / past the
 * timeout ceiling — normal state stays silent so the UI does not nag
 * during healthy executions.
 */
const TimeoutAwareProgressBar: FC<{
  percentage: number;
  status: ExecutionStatus;
  phase: ProgressPhase;
  preFlightResult: PreFlightResult | null;
  timeoutState: TimeoutState;
  remainingMs: number | null;
}> = ({
  percentage,
  status,
  phase,
  preFlightResult,
  timeoutState,
  remainingMs,
}) => {
  const t = useI18n();
  const bar = (
    <ProgressBar
      percentage={percentage}
      status={status}
      phase={phase}
      preFlightResult={preFlightResult}
      timeoutState={timeoutState}
    />
  );

  if (timeoutState === 'normal' || status !== 'running') {
    return bar;
  }

  const tooltip =
    timeoutState === 'error'
      ? t['com.streamlain.streamlain.execution.timeout.reached']()
      : t['com.streamlain.streamlain.execution.timeout.approaching']({
          remaining: String(Math.ceil((remainingMs ?? 0) / 1000)),
        });

  return (
    <Tooltip content={tooltip} side="top">
      <div className={runStyles.tooltipAnchor}>{bar}</div>
    </Tooltip>
  );
};

// =============================================================================
// Elapsed Timer Hook
// =============================================================================

function useElapsedTimer(isRunning: boolean, startTime: number | null): string {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!isRunning || !startTime) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isRunning, startTime]);

  if (!startTime) return '0:00';
  const elapsed = Math.max(
    0,
    Math.floor(((isRunning ? now : Date.now()) - startTime) / 1000)
  );
  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * sl-3nkm.8.4: Convert the elapsed-since-start signal into a
 * normal/warning/error state gated on the plan's `maxStepDurationMs`.
 * Emits on the same 1-second tick as `useElapsedTimer`; when
 * `maxStepDurationMs` is missing (limits still loading) we return
 * `normal` + `null` so the UI behaves like pre-8.4.
 */
function useTimeoutWarning(
  isRunning: boolean,
  startTime: number | null,
  maxStepDurationMs: number | null
): { state: TimeoutState; remainingMs: number | null } {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!isRunning || !startTime || !maxStepDurationMs) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isRunning, startTime, maxStepDurationMs]);

  if (!isRunning || !startTime || !maxStepDurationMs) {
    return { state: 'normal', remainingMs: null };
  }
  const elapsedMs = Math.max(0, now - startTime);
  return computeTimeoutState(elapsedMs, maxStepDurationMs);
}

// =============================================================================
// E029-PREFLIGHT: Status indicator + Stats Line (clean, no glow)
// =============================================================================

/** Clean status color — no text-shadow, just subtle color differentiation */
const statusTone = (
  active: boolean,
  done: boolean,
  failed: boolean
): runStyles.ExecutionTone => {
  if (failed) return 'error';
  if (active) return 'active';
  if (done) return 'success';
  return 'idle';
};

const StatusDot: FC = () => (
  <span className={runStyles.dot} aria-hidden="true" />
);

type I18nRecord = Record<
  string,
  ((params?: Record<string, string>) => string) | undefined
>;

function text(
  t: unknown,
  key: string,
  fallback: string,
  params?: Record<string, string>
): string {
  return (t as I18nRecord)[key]?.(params) ?? fallback;
}

function userFacingExecutionError(t: unknown, error: string): string {
  const localized = formatWorkflowErrorMessage(t, error);
  if (localized) return localized;
  const normalized = error.toLowerCase();
  if (
    normalized.includes('warm_pool_enabled') ||
    normalized.includes('warm pool dispatch') ||
    normalized.includes('k8s mode')
  ) {
    return text(
      t,
      'com.streamlain.streamlain.execution.error.runner-unavailable',
      'Die Ausführung ist gerade nicht verfügbar. Bitte später erneut versuchen.'
    );
  }
  if (
    normalized.includes('failed to fetch') ||
    normalized.includes('networkerror') ||
    normalized.includes('connection timeout') ||
    normalized.includes('backend unreachable')
  ) {
    return text(
      t,
      'com.streamlain.streamlain.execution.error.backend-unavailable',
      'Der Backend-Dienst ist gerade nicht erreichbar. Bitte später erneut versuchen.'
    );
  }
  return error;
}

const ProgressStats: FC<{
  state: ExecutionState;
  preFlightResult: PreFlightResult | null;
  phase: ProgressPhase;
  preFlightExpanded: boolean;
  onPreFlightClick: () => void;
  elapsedTime: string;
}> = ({
  state,
  preFlightResult,
  phase,
  preFlightExpanded,
  onPreFlightClick,
  elapsedTime,
}) => {
  const t = useI18n();
  const { progress } = state;

  const preFlightStatus = getPreFlightDisplayStatus(preFlightResult);
  const preFlightDone = preFlightStatus === 'done';
  const preFlightFailed = preFlightStatus === 'failed';
  const preFlightRunning = preFlightStatus === 'running';

  const executionActive = phase === 'execution' && state.status === 'running';
  const executionDone =
    phase === 'execution' &&
    (state.status === 'completed' || state.status === 'cancelled');
  const executionFailed = phase === 'execution' && state.status === 'failed';

  const pfTone = statusTone(preFlightRunning, preFlightDone, preFlightFailed);
  const exTone = statusTone(executionActive, executionDone, executionFailed);

  const pfLabel = preFlightFailed
    ? t['com.streamlain.streamlain.execution.preflight.failed']()
    : preFlightDone
      ? t['com.streamlain.streamlain.execution.preflight.ok']()
      : preFlightRunning
        ? t['com.streamlain.streamlain.execution.preflight.verifying']()
        : text(
            t,
            'com.streamlain.streamlain.execution.preflight',
            'Vorbereitung'
          );

  return (
    <div className={runStyles.stats}>
      {/* Preflight status: toggles the detail table once a result exists */}
      {preFlightResult ? (
        <button
          type="button"
          className={clsx(runStyles.statsToggle, runStyles.toneText[pfTone])}
          aria-expanded={preFlightExpanded}
          onClick={onPreFlightClick}
        >
          <StatusDot />
          {pfLabel}
          <DisclosureChevron
            expanded={preFlightExpanded}
            className={runStyles.statsChevron}
          />
        </button>
      ) : (
        <span className={clsx(runStyles.statsItem, runStyles.toneText[pfTone])}>
          <StatusDot />
          {pfLabel}
        </span>
      )}

      {/* Row progress + results (always visible, dimmed when inactive) */}
      <span className={clsx(runStyles.statsItem, runStyles.toneText[exTone])}>
        <StatusDot />
        {t['com.streamlain.streamlain.execution.rows']({
          processed: String(progress.processed),
          total: String(progress.total),
        })}
      </span>

      {progress.media && (
        <span
          className={clsx(
            runStyles.tabular,
            runStyles.toneText[executionActive ? 'active' : 'neutral']
          )}
        >
          {[
            progress.media.percent !== undefined
              ? `${progress.media.percent}%`
              : null,
            progress.media.bitrate ?? null,
            progress.media.fps !== undefined
              ? `${progress.media.fps.toFixed(1)} fps`
              : null,
            progress.media.timeRemainingMs !== undefined
              ? `ETA ${Math.max(0, Math.ceil(progress.media.timeRemainingMs / 1000))}s`
              : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      )}

      {/* Elapsed time — always visible, shows 0:00 when idle */}
      <span
        className={clsx(
          runStyles.elapsed,
          runStyles.toneText[executionActive ? 'active' : 'idle']
        )}
      >
        {elapsedTime}
      </span>
    </div>
  );
};

// =============================================================================
// E029-PREFLIGHT: Expandable Detail Table
// =============================================================================

const PreFlightExpandable: FC<{
  result: PreFlightResult;
  expanded: boolean;
}> = ({ result, expanded }) => {
  const t = useI18n();

  const handleCopy = useCallback(() => {
    const lines: string[] = [];
    lines.push(`Pre-Flight Verification (${result.profile})`);
    lines.push('');
    for (const step of result.steps) {
      const dur = step.durationMs !== undefined ? ` ${step.durationMs}ms` : '';
      const icon =
        step.status === 'done'
          ? '[ok]'
          : step.status === 'failed'
            ? '[x]'
            : '[ ]';
      lines.push(`${icon} ${step.name}${dur}`);
      if (step.error) lines.push(`  Error: ${step.error}`);
    }
    lines.push('');
    lines.push(
      `Attributes: ${result.matchedAttributes}/${result.totalAttributes}`
    );
    lines.push('');
    lines.push('Attribute            Local              Backend            OK');
    lines.push('-'.repeat(72));
    for (const attr of result.attributes) {
      const icon = attr.match ? '[ok]' : '[x]';
      const l = attr.localValue.padEnd(18).slice(0, 18);
      const b = attr.backendValue.padEnd(18).slice(0, 18);
      lines.push(`${attr.name.padEnd(20)} ${l} ${b} ${icon}`);
    }
    navigator.clipboard
      .writeText(lines.join('\n'))
      .then(() => {
        notify.success({
          title: t['com.streamlain.streamlain.execution.preflight.copied'](),
          message:
            t['com.streamlain.streamlain.execution.preflight.copied-message'](),
        });
      })
      .catch(() => {
        /* clipboard write failed */
      });
  }, [result, t]);

  return (
    <AnimatedRegion expanded={expanded}>
      <div className={runStyles.preflight}>
        <div className={runStyles.preflightHeader}>
          <span className={runStyles.preflightTitle}>
            {t['com.streamlain.streamlain.execution.preflight.title']({
              profile: result.profile,
            })}
          </span>
          <button
            type="button"
            className={runStyles.smallButton}
            onClick={handleCopy}
          >
            {t['com.streamlain.streamlain.execution.preflight.copy']()}
          </button>
        </div>

        {/* Phase steps */}
        {result.steps.map((step, i) => {
          const tone: runStyles.ExecutionTone =
            step.status === 'done'
              ? 'success'
              : step.status === 'failed'
                ? 'error'
                : step.status === 'running'
                  ? 'active'
                  : 'neutral';
          return (
            <div
              key={i}
              className={clsx(
                runStyles.preflightStep,
                runStyles.toneText[tone]
              )}
            >
              <StatusDot />
              {t['com.streamlain.streamlain.execution.preflight.phase']({
                number: String(i + 1),
                name: step.name,
              })}
              {step.durationMs !== undefined && (
                <span className={clsx(runStyles.muted, runStyles.tabular)}>
                  {step.durationMs} ms
                </span>
              )}
              {step.error && (
                <span className={runStyles.toneText.error}>— {step.error}</span>
              )}
            </div>
          );
        })}

        {/* Attribute details */}
        <div className={runStyles.preflightAttributes}>
          <div className={runStyles.preflightAttributesTitle}>
            {t['com.streamlain.streamlain.execution.preflight.attributes']({
              matched: String(result.matchedAttributes),
              total: String(result.totalAttributes),
            })}
          </div>
          {result.attributes.map((attr, i) => (
            <div
              key={i}
              className={runStyles.attributeRow}
              data-match={attr.match}
            >
              <span className={runStyles.attributeName} title={attr.name}>
                {attr.name}
              </span>
              <span
                className={runStyles.attributeValue}
                title={attr.localValue}
              >
                {attr.localValue}
              </span>
              <span
                className={runStyles.attributeValue}
                title={attr.backendValue}
              >
                {attr.backendValue}
              </span>
              <span
                className={runStyles.toneText[attr.match ? 'success' : 'error']}
              >
                <StatusDot />
              </span>
            </div>
          ))}
        </div>
      </div>
    </AnimatedRegion>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export const ExecutionPanel: FC<ExecutionPanelProps> = ({
  layout,
  runLocked = false,
  runLockedHint,
  onRunLockedClick,
  onTestCreated,
  blockId,
  rows = [],
  workflow,
  config,
  accessibleDatabases = [],
  canExecute = true,
  chosenRows,
}) => {
  const executionService = useService(ExecutionService);
  const loggingService = useService(LoggingService);
  const workflowClient = useService(WorkflowClientService);
  const workflowService = useService(WorkflowService);
  const workflowTestContext = useService(WorkflowTestContextService);
  const workspaceService = useService(WorkspaceService);
  const databaseDiscoveryService = useService(DatabaseDiscoveryService);
  const docsService = useService(DocsService);
  const workflowOnline = useLiveData(workflowService.connectivity$);
  const showWorkflowOffline = useUiOfflineSignal(workflowOnline === false);
  const isOnline = !showWorkflowOffline;
  // sl-g3on.5.3: Used by WarningRow to resolve field IDs to display names.
  const selectedWorkflowSchema = useLiveData(
    workflowService.selectedWorkflowSchema$
  );
  const workspaceDialogService = useService(WorkspaceDialogService);
  const editorService = useServiceOptional(EditorService);
  const blockModel = useAutomationBlockModel(blockId);
  const state = useLiveData(executionService.getState$(blockId));
  // Get workspace and doc IDs for backend persistence
  const workspaceId = workspaceService.workspace.id;
  const editorContainer = useLiveData(
    editorService?.editor?.editorContainer$ ?? null
  );
  const docId = editorContainer?.doc?.id ?? 'unknown';

  const canStartFromState = executionService.canStart(blockId);
  const canStart = canStartFromState && isOnline;

  const canStop = executionService.canStop(blockId);

  // State for Run Requirements Dialog
  const [requirementsDialogMode, setRequirementsDialogMode] = useState<
    'run' | 'test' | null
  >(null);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [creatingTestRun, setCreatingTestRun] = useState(false);
  const activeTestRun = useLiveData(workflowTestContext.activeRun$);

  // E029-PREFLIGHT: Pre-Flight state
  const [preFlightResult, setPreFlightResult] =
    useState<PreFlightResult | null>(null);
  const [preFlightExpanded, setPreFlightExpanded] = useState(false);
  const [progressPhase, setProgressPhase] =
    useState<ProgressPhase>('preflight');
  const preFlightRef = useRef<PreFlightResult | null>(null);
  const preFlightDatabaseStorageRefs = useMemo(
    () =>
      accessibleDatabases.map(database => ({
        tableId: database.blockId,
        docId: database.docId,
        databaseRootDocId: database.databaseRootDocId,
        databaseDirectoryPartDocIds: database.databaseDirectoryPartDocIds,
        databaseRowShardDocIds: database.databaseRowShardDocIds,
      })),
    [accessibleDatabases]
  );
  const preFlightDirectories = useMemo(
    () => preFlightDatabaseDirectories(databaseDiscoveryService, docsService),
    [databaseDiscoveryService, docsService]
  );

  // sl-g3on.5.3: Last execution's structured warnings — rendered below the
  // run controls until the next execution clears them.
  const [lastWarnings, setLastWarnings] = useState<ExecutionWarning[] | null>(
    null
  );
  // sl-g3on.9.2: Last execution's per-row retry / on-failure metadata.
  const [lastExecutionRows, setLastExecutionRows] = useState<
    ProcessStageRowEntry[] | null
  >(null);

  const t = useI18n();
  const offlineExecutionTitle = text(
    t,
    'com.streamlain.streamlain.execution.notify.backend-unavailable-title',
    'Ausführung gerade nicht verfügbar'
  );
  const offlineExecutionMessage =
    t['com.streamlain.streamlain.connectivity.offline-warning']();
  const showOfflineExecutionToast = useCallback(() => {
    notify.warning({
      title: offlineExecutionTitle,
      message: offlineExecutionMessage,
    });
  }, [offlineExecutionMessage, offlineExecutionTitle]);
  const hasConfiguredSource = hasConfiguredAutomationInput(config);
  const isWebhook = config?.triggerConfig?.type === 'webhook';
  const hasInputTable = !!config?.sourceTableId;

  // Calculate requirements for Run button
  const hasOutputTable = (config?.outputTables ?? []).length > 0;
  const hasOutputMappings = (config?.outputMappings ?? []).some(
    m => !!m.responseField
  );
  const runRequirements: RunRequirement[] = useMemo(() => {
    // The input table is optional: without one the workflow runs once per
    // trigger, so only a webhook source and, with a table, its rows count.
    // The output is optional too: without a table nothing is written back,
    // so there is nothing to map.
    return [
      ...(isWebhook
        ? [
            {
              id: 'source_table',
              label:
                t[
                  'com.streamlain.streamlain.execution.preflight.webhook-source'
                ](),
              met: hasConfiguredSource,
              description:
                t[
                  'com.streamlain.streamlain.execution.preflight.webhook-source-desc'
                ](),
            },
          ]
        : []),
      {
        id: 'workflow',
        label: t['com.streamlain.streamlain.execution.preflight.workflow'](),
        met: !!config?.workflowId,
        description:
          t['com.streamlain.streamlain.execution.preflight.workflow-desc'](),
      },
      // Chosen rows run whatever their status says: Run counts them and
      // explains how to choose when none is chosen.
      ...(hasInputTable && !chosenRows
        ? [
            {
              id: 'rows_available',
              label: t['com.streamlain.streamlain.execution.preflight.rows'](),
              met: rows.length > 0,
              description:
                rows.length === 0
                  ? t[
                      'com.streamlain.streamlain.execution.preflight.rows-desc'
                    ]()
                  : undefined,
            },
          ]
        : []),
      ...(hasOutputTable
        ? [
            {
              id: 'output_mapping',
              label:
                t['com.streamlain.streamlain.execution.preflight.mapping'](),
              met: hasOutputMappings,
              description:
                t[
                  'com.streamlain.streamlain.execution.preflight.mapping-desc'
                ](),
            },
          ]
        : []),
    ];
  }, [
    hasConfiguredSource,
    isWebhook,
    hasInputTable,
    chosenRows,
    config?.workflowId,
    rows.length,
    hasOutputTable,
    hasOutputMappings,
    t,
  ]);

  const allRequirementsMet = runRequirements.every(r => r.met);
  const testRequirements = useMemo(
    () => runRequirements.filter(r => r.id !== 'output_mapping'),
    [runRequirements]
  );
  const allTestRequirementsMet = testRequirements.every(r => r.met);
  const activeAutomationTestRun =
    activeTestRun?.run.automationBlockId === blockId &&
    activeTestRun.run.workflowId === config?.workflowId
      ? activeTestRun
      : null;
  const activeTestSourceDocCount =
    activeAutomationTestRun?.run.sourceDocIds.length ?? 0;
  const activeTestRowCount = Array.isArray(
    (activeAutomationTestRun?.run.inputPayload as { rows?: unknown })?.rows
  )
    ? (
        (activeAutomationTestRun?.run.inputPayload as { rows?: unknown[] })
          .rows ?? []
      ).length
    : 0;
  const activeTestExpiresAt = activeAutomationTestRun
    ? new Date(activeAutomationTestRun.run.expiresAt).toLocaleString()
    : null;

  /**
   * Persist log and update runtime status after execution completes
   * Also fetches workflow engine execution details for nodeSteps
   */
  const handleExecutionComplete = useCallback(
    async (status: 'success' | 'error' | 'cancelled') => {
      // Get the completed log from history (not activeLog, which is null after completeLog)
      // completeLog() adds to history then sets activeLog = null, so we get from history
      const completedLog = loggingService.getLatestLog(blockId);

      if (!completedLog || !blockModel || !blockModel.store) {
        console.warn('[ExecutionPanel] Cannot persist: missing data');
        return;
      }

      // Try to fetch nodeSteps from workflow engine execution details
      if (workflow?.id && completedLog.process) {
        try {
          // Small delay to ensure workflow engine has finished processing
          await new Promise(resolve => setTimeout(resolve, 500));

          const execution = await workflowClient.getLatestExecution(
            workflow.id
          );
          if (execution) {
            const nodeSteps =
              workflowClient.parseExecutionToNodeSteps(execution);

            // Update the log with nodeSteps
            completedLog.process.nodeSteps = nodeSteps;
            completedLog.process.executionId = execution.id;
          }
        } catch (error) {
          notify.warning({
            title: t['com.streamlain.streamlain.workflow.failed-to-load'](),
            message: error instanceof Error ? error.message : String(error),
          });
        }
      }

      try {
        // Persist log to backend (Single Source of Truth)
        // This is done HERE after fetching nodeSteps so all execution data is included
        const backendStatus =
          status === 'success'
            ? 'completed'
            : status === 'error'
              ? 'failed'
              : 'cancelled';

        const response = await workspaceApiFetch(
          workflowWorkspaceRoutes.logs(workspaceId),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              workspaceId,
              docId,
              blockId,
              mode: completedLog.mode,
              status: backendStatus,
              startedAt: new Date(completedLog.startedAt).toISOString(),
              completedAt: new Date(
                completedLog.completedAt || Date.now()
              ).toISOString(),
              inputStage: completedLog.input
                ? {
                    sourceTableId: completedLog.input.sourceTableId,
                    sourceTableName: completedLog.input.sourceTable,
                    rowCount: completedLog.input.passedFilter,
                    rows: completedLog.input.payloads,
                  }
                : undefined,
              processStage: completedLog.process
                ? {
                    webhookUrl:
                      workflow?.webhookUrl || config?.webhookUrlOverride,
                    duration: completedLog.process.duration,
                    responseStatus:
                      completedLog.process.status === 'success' ? 200 : 500,
                    responseData: completedLog.process.response,
                    nodeSteps: completedLog.process.nodeSteps,
                    executionId: completedLog.process.executionId,
                    workflowName: completedLog.process.workflowName,
                  }
                : undefined,
              outputStage: completedLog.output
                ? {
                    targetTableId: completedLog.output.targetTableId,
                    targetTableName: completedLog.output.targetTable,
                    rowsWritten: completedLog.output.rowsWritten,
                  }
                : undefined,
              error: completedLog.error,
            }),
          },
          {
            workspaceId,
            category: 'foreground-action',
            mode: 'foreground',
            priority: 'foreground',
          }
        );

        if (!response.ok) {
          console.error(
            '[ExecutionPanel] Failed to persist log to backend:',
            response.status
          );
        }
      } catch (error) {
        console.error('[ExecutionPanel] Error persisting log:', error);
      }
    },
    [
      t,
      loggingService,
      blockId,
      blockModel,
      workflow?.id,
      workflowClient,
      workspaceId,
      docId,
      workflow?.webhookUrl,
      config?.webhookUrlOverride,
    ]
  );

  // E029-PREFLIGHT: Pre-Flight Sync with local verification + backend verification
  const workspace = workspaceService.workspace;
  const configApi = useService(AutomationConfigApiService);

  /**
   * E029-PREFLIGHT: Shared pre-flight sync for execute and side-effect-free
   * workflow test data preparation.
   *
   * 1. Verify local block props contain required fields
   * 2. Flush Y.js to backend (flushAndWaitForSynced)
   * 3. Call verify-sync endpoint - backend reads Y.js and returns what it sees
   * 4. Compare local vs backend - field-by-field based on profile
   *
   * Profile: 'execute' — full config comparison including output writes.
   * Profile: 'test' — source/workflow/input comparison only.
   */
  const preFlightSync = useCallback(
    async (profile: PreFlightProfile): Promise<boolean> => {
      // Reset state
      setProgressPhase('preflight');
      setPreFlightExpanded(false);

      const steps: PreFlightStep[] = [
        {
          name: t['com.streamlain.streamlain.execution.step.config-check'](),
          status: 'pending',
        },
        { name: 'Y.js Sync', status: 'pending' },
        {
          name: t['com.streamlain.streamlain.execution.step.backend-verify'](),
          status: 'pending',
        },
        {
          name: t['com.streamlain.streamlain.execution.step.config-compare'](),
          status: 'pending',
        },
      ];

      const updateResult = (partial: Partial<PreFlightResult>) => {
        const current: PreFlightResult = {
          profile,
          steps: [...steps],
          attributes: [],
          totalAttributes: 0,
          matchedAttributes: 0,
          overallSuccess: false,
          ...partial,
        };
        preFlightRef.current = current;
        setPreFlightResult({ ...current });
      };

      const failStep = (idx: number, error: string) => {
        steps[idx].status = 'failed';
        steps[idx].error = error;
        updateResult({ overallSuccess: false });
      };

      const notifyBackendUnavailable = () => {
        notify.error({
          title: text(
            t,
            'com.streamlain.streamlain.execution.notify.backend-unavailable-title',
            'Ausführung gerade nicht verfügbar'
          ),
          message: text(
            t,
            'com.streamlain.streamlain.execution.notify.backend-unavailable-message',
            'Der Backend-Dienst ist gerade nicht erreichbar. Bitte später erneut versuchen.'
          ),
        });
      };

      const notifySyncNotReady = () => {
        notify.error({
          title: text(
            t,
            'com.streamlain.streamlain.execution.notify.prepare-failed-title',
            'Ausführung noch nicht möglich'
          ),
          message: text(
            t,
            'com.streamlain.streamlain.execution.notify.sync-not-ready-message',
            'Die Änderungen sind noch nicht vollständig synchronisiert. Bitte gleich erneut versuchen.'
          ),
        });
      };

      // Publish initial state
      updateResult({});

      // Step 1: Config Check
      const t1 = performance.now();
      steps[0].status = 'running';
      updateResult({});

      if (!blockModel) {
        failStep(0, 'Block model not available');
        notify.error({
          title:
            t['com.streamlain.streamlain.execution.error.block-not-found'](),
          message: t['com.streamlain.automation.sidebar.block-unavailable'](),
        });
        return false;
      }

      const localConfig = extractConfig(blockModel);
      if (!hasConfiguredAutomationInput(localConfig)) {
        failStep(0, 'No webhook source configured');
        notify.error({
          title:
            t['com.streamlain.streamlain.execution.error.config-incomplete'](),
          message:
            t[
              'com.streamlain.streamlain.execution.notify.webhook-source-required'
            ](),
        });
        return false;
      }
      if (!localConfig?.workflowId && !localConfig?.webhookUrlOverride) {
        failStep(0, 'No workflow connected');
        notify.error({
          title:
            t['com.streamlain.streamlain.execution.error.config-incomplete'](),
          message: t['com.streamlain.streamlain.execution.missing-workflow'](),
        });
        return false;
      }

      if (
        profile === 'execute' &&
        localConfig.statusTracking?.enabled &&
        localConfig.statusTracking.mode !== 'delete_row' &&
        !localConfig.statusTracking.columnId
      ) {
        failStep(0, 'Status tracking column not configured');
        notify.error({
          title:
            t['com.streamlain.streamlain.execution.error.config-incomplete'](),
          message:
            t[
              'com.streamlain.streamlain.validation.status-tracking-incomplete'
            ](),
        });
        return false;
      }

      const staleMappings = getStaleWorkflowMappingIds(
        profile,
        localConfig,
        selectedWorkflowSchema
      );
      if (staleMappings.input.length > 0) {
        failStep(0, 'Stale input mappings');
        notify.error({
          title: t['com.streamlain.automation.sidebar.stale-input.title'](),
          message: t['com.streamlain.automation.sidebar.stale-input.message'](),
        });
        return false;
      }

      if (staleMappings.output.length > 0) {
        failStep(0, 'Stale output mappings');
        notify.error({
          title: t['com.streamlain.automation.sidebar.stale-output.title'](),
          message:
            t['com.streamlain.automation.sidebar.stale-output.message'](),
        });
        return false;
      }

      if (profile === 'execute') {
        // Check for unconfigured output targets (empty tableId)
        const emptyTargets = (localConfig.outputTables ?? []).filter(
          t => !t.tableId
        );
        if (emptyTargets.length > 0) {
          failStep(0, `${emptyTargets.length} unconfigured output target(s)`);
          notify.error({
            title:
              t['com.streamlain.streamlain.execution.missing-output-table'](),
            message:
              t['com.streamlain.automation.sidebar.empty-target.message'](),
          });
          return false;
        }

        // A run that writes into a table needs at least one mapped result;
        // a run without output table writes nothing back.
        if (
          (localConfig.outputTables ?? []).length > 0 &&
          !(localConfig.outputMappings ?? []).some(m => !!m.responseField)
        ) {
          failStep(0, 'No output field mapping configured');
          notify.error({
            title:
              t['com.streamlain.streamlain.execution.missing-output-mapping'](),
            message:
              t[
                'com.streamlain.automation.sidebar.no-output-mapping.message'
              ](),
          });
          return false;
        }
      }

      steps[0].status = 'done';
      steps[0].durationMs = Math.round(performance.now() - t1);
      updateResult({});

      // Step 2: Y.js Flush
      const t2 = performance.now();
      steps[1].status = 'running';
      updateResult({});

      try {
        await flushPreFlightDocs(
          workspace.engine.doc,
          {
            automationDocId: docId,
            config: localConfig,
            profile,
            databaseStorageRefs: preFlightDatabaseStorageRefs,
            workspaceId,
          },
          preFlightLocalDocs(workspace),
          preFlightDirectories
        );
      } catch (syncError) {
        failStep(
          1,
          syncError instanceof Error ? syncError.message : 'Flush failed'
        );
        notify.error({
          title: text(
            t,
            'com.streamlain.streamlain.execution.notify.prepare-failed-title',
            'Ausführung noch nicht möglich'
          ),
          message: text(
            t,
            'com.streamlain.streamlain.execution.notify.sync-not-ready-message',
            'Die Änderungen sind noch nicht vollständig synchronisiert. Bitte gleich erneut versuchen.'
          ),
        });
        return false;
      }

      steps[1].status = 'done';
      steps[1].durationMs = Math.round(performance.now() - t2);
      updateResult({});

      // Step 3: Backend Verify
      const t3 = performance.now();
      steps[2].status = 'running';
      updateResult({});

      let backendConfig: VerifySyncConfig;
      try {
        const verifyResult = await configApi.verifySyncConfig(
          blockId,
          workspaceId,
          docId
        );

        if (!verifyResult.verified || !verifyResult.config) {
          failStep(2, verifyResult.error || 'Backend verification failed');
          if (isVerifySyncBackendUnavailable(verifyResult)) {
            notifyBackendUnavailable();
          } else {
            notifySyncNotReady();
          }
          return false;
        }

        backendConfig = verifyResult.config;
      } catch (verifyError) {
        failStep(
          2,
          verifyError instanceof Error
            ? verifyError.message
            : 'Verify request failed'
        );
        notify.error({
          title: text(
            t,
            'com.streamlain.streamlain.execution.notify.backend-unavailable-title',
            'Ausführung gerade nicht verfügbar'
          ),
          message: text(
            t,
            'com.streamlain.streamlain.execution.notify.backend-unavailable-message',
            'Der Backend-Dienst ist gerade nicht erreichbar. Bitte später erneut versuchen.'
          ),
        });
        return false;
      }

      steps[2].status = 'done';
      steps[2].durationMs = Math.round(performance.now() - t3);
      updateResult({});

      // Step 4: Config Compare (profile-dependent)
      // Auto-retry once on mismatch: sporadic sync races can cause the backend
      // snapshot to lag behind the frontend Y.js state despite flush succeeding.
      const MAX_COMPARE_ATTEMPTS = 2;
      const COMPARE_RETRY_DELAY_MS = 500;
      let compareAttempt = 0;
      let attributes: PreFlightAttribute[] = [];
      let matchedCount = 0;
      let allMatch = false;

      const t4 = performance.now();
      steps[3].status = 'running';
      updateResult({});

      while (compareAttempt < MAX_COMPARE_ATTEMPTS) {
        attributes = buildPreFlightAttributes(
          profile,
          localConfig,
          backendConfig
        );

        matchedCount = attributes.filter(a => a.match).length;
        allMatch = matchedCount === attributes.length;

        if (allMatch || compareAttempt >= MAX_COMPARE_ATTEMPTS - 1) break;

        // Retry: wait, re-flush, re-verify
        await new Promise(r => setTimeout(r, COMPARE_RETRY_DELAY_MS));
        try {
          await flushPreFlightDocs(
            workspace.engine.doc,
            {
              automationDocId: docId,
              config: localConfig,
              profile,
              databaseStorageRefs: preFlightDatabaseStorageRefs,
              workspaceId,
            },
            preFlightLocalDocs(workspace),
            preFlightDirectories
          );
          const retryResult = await configApi.verifySyncConfig(
            blockId,
            workspaceId,
            docId
          );
          if (isVerifySyncBackendUnavailable(retryResult)) {
            steps[3].status = 'failed';
            steps[3].durationMs = Math.round(performance.now() - t4);
            steps[3].error = retryResult.error || 'Backend verification failed';
            updateResult({ overallSuccess: false });
            notifyBackendUnavailable();
            return false;
          }
          if (retryResult.verified && retryResult.config) {
            backendConfig = retryResult.config;
          }
        } catch {
          // Retry failed — fall through to mismatch handling
        }
        compareAttempt++;
      }

      if (!allMatch) {
        steps[3].status = 'failed';
        steps[3].durationMs = Math.round(performance.now() - t4);
        steps[3].error = `${attributes.length - matchedCount} mismatch(es)`;
        updateResult({
          attributes,
          totalAttributes: attributes.length,
          matchedAttributes: matchedCount,
          overallSuccess: false,
        });

        const mismatches = attributes
          .filter(a => !a.match)
          .map(a => a.name)
          .join(', ');
        notify.error({
          title: text(
            t,
            'com.streamlain.streamlain.execution.notify.prepare-failed-title',
            'Ausführung noch nicht möglich'
          ),
          message: text(
            t,
            'com.streamlain.streamlain.execution.notify.config-mismatch-message',
            'Die Automation ist noch nicht vollständig synchronisiert. Bitte gleich erneut versuchen.'
          ),
        });
        console.warn('[ExecutionPanel] Config sync mismatch:', mismatches);
        setPreFlightExpanded(true);
        return false;
      }

      steps[3].status = 'done';
      steps[3].durationMs = Math.round(performance.now() - t4);
      updateResult({
        attributes,
        totalAttributes: attributes.length,
        matchedAttributes: matchedCount,
        overallSuccess: true,
      });

      // Transition to execution phase
      setProgressPhase('execution');
      return true;
    },
    [
      blockModel,
      workspace,
      docId,
      workspaceId,
      blockId,
      configApi,
      selectedWorkflowSchema,
      preFlightDatabaseStorageRefs,
      preFlightDirectories,
      t,
    ]
  );

  // T047: Calculate actual row count based on batchSize
  const batchSize = config?.batchConfig?.batchSize ?? 1;
  // Without an input table a run is the trigger's single row.
  const actualRowCount = config?.sourceTableId
    ? Math.min(rows.length, batchSize)
    : 1;
  const isBatchMode = config?.batchConfig?.executionMode === 'batch';

  // sl-3yai.7.2 + sl-za0v.10: Block execution if compliance guard or RBAC fails
  const refusedWithoutPermission = useCallback((): boolean => {
    if (canExecute) return false;
    notify.error({
      title:
        t[
          'com.streamlain.streamlain.execution.notify.permission-denied-title'
        ](),
      message:
        t[
          'com.streamlain.streamlain.execution.notify.permission-denied-message'
        ](),
    });
    return true;
  }, [canExecute, t]);

  // E029-PREFLIGHT: Execute with full profile
  const handleExecute = useCallback(async (): Promise<void> => {
    if (refusedWithoutPermission()) return;

    const synced = await preFlightSync('execute');
    if (!synced) return;

    executionService
      .executeViaBackend(workspaceId, docId, blockId, 'execute', actualRowCount)
      .then(result => {
        if (!result.success) {
          // Failure is already surfaced by AutomationSocketService via the
          // `execution:update` websocket event (classified message + "Open
          // Workflow" action). Showing a second toast here spammed the user
          // with three notifications for the same event (see sl-oe5x smoke
          // test). Fall through so `refreshLogs` below still runs.
        } else if (result.warnings && result.warnings.length > 0) {
          // sl-g3on.5.3: Surface backend warnings as a single toast
          // ("N warnings — see Execution panel for details") and stash
          // them on local state so the panel renders one WarningRow per
          // entry. The detail view is the source of truth; the toast is
          // only the wakeup hint.
          setLastWarnings(result.warnings);
          notify.warning({
            title: t['com.streamlain.streamlain.execution.warnings.title']({
              count: String(result.warnings.length),
            }),
            message:
              t['com.streamlain.streamlain.execution.warnings.see-panel'](),
          });
        } else {
          setLastWarnings(null);
        }
        loggingService.refreshLogs(blockId, workspaceId);
        // sl-g3on.9.2: Pull per-row retry metadata from the most recent
        // log right after refresh — it carries the rows[] array the
        // backend orchestrator now persists into processStage.
        const latest = loggingService.getLatestLog(blockId);
        const rows = latest?.process?.rows;
        setLastExecutionRows(rows && rows.length > 0 ? rows : null);
      })
      .catch((error: Error) => {
        console.error('Execution failed:', error);
        notify.error({
          title: text(
            t,
            'com.streamlain.streamlain.execution.notify.backend-unavailable-title',
            'Ausführung gerade nicht verfügbar'
          ),
          message: userFacingExecutionError(t, error.message),
        });
        loggingService.refreshLogs(blockId, workspaceId);
      });
  }, [
    preFlightSync,
    executionService,
    workspaceId,
    docId,
    blockId,
    loggingService,
    refusedWithoutPermission,
    t,
    actualRowCount,
  ]);

  const [chooseRowsOpen, setChooseRowsOpen] = useState(false);
  const chosenRowIds = chosenRows?.rowIds;
  // A manual run over the chosen rows: queued runs report like any run.
  const handleRunRows = useCallback(async (): Promise<void> => {
    if (!chosenRowIds?.length || refusedWithoutPermission()) return;
    const synced = await preFlightSync('execute');
    if (!synced) return;
    try {
      const result = await executionService.runRows(
        workspaceId,
        docId,
        blockId,
        chosenRowIds
      );
      if (!result.success) {
        notify.error({
          title: text(
            t,
            'com.streamlain.streamlain.execution.notify.backend-unavailable-title',
            'Ausführung gerade nicht verfügbar'
          ),
          message: userFacingExecutionError(t, result.error),
        });
      } else if (result.rows === 0) {
        notify.warning({
          title: t['com.streamlain.automation.run-rows.none-found.title'](),
          message: t['com.streamlain.automation.run-rows.none-found.message'](),
        });
      } else {
        notify.success({
          title: t['com.streamlain.automation.run-rows.queued.title']({
            runs: String(result.runs),
          }),
          message: t['com.streamlain.automation.run-rows.queued.message']({
            rows: String(result.rows),
          }),
        });
      }
    } catch (error) {
      notify.error({
        title: text(
          t,
          'com.streamlain.streamlain.execution.notify.backend-unavailable-title',
          'Ausführung gerade nicht verfügbar'
        ),
        message: userFacingExecutionError(
          t,
          error instanceof Error ? error.message : String(error)
        ),
      });
    }
    loggingService.refreshLogs(blockId, workspaceId);
  }, [
    chosenRowIds,
    refusedWithoutPermission,
    preFlightSync,
    executionService,
    workspaceId,
    docId,
    blockId,
    loggingService,
    t,
  ]);
  const runCount = chosenRows ? chosenRows.rowIds.length : actualRowCount;

  const handleCreateTestRun = useCallback(async (): Promise<void> => {
    if (!canExecute) {
      notify.error({
        title:
          t[
            'com.streamlain.streamlain.execution.notify.permission-denied-title'
          ](),
        message:
          t[
            'com.streamlain.streamlain.execution.notify.permission-denied-message'
          ](),
      });
      return;
    }

    setCreatingTestRun(true);
    try {
      const synced = await preFlightSync('test');
      if (!synced) return;

      await workflowTestContext.createFromAutomation({
        workspaceId,
        docId,
        blockId,
      });
      notify.success({
        title: t['com.streamlain.streamlain.execution.test.created-title'](),
        message:
          t['com.streamlain.streamlain.execution.test.created-message'](),
      });
      setShowTestDialog(false);
      onTestCreated?.();
    } catch (error) {
      notify.error({
        title:
          t['com.streamlain.streamlain.execution.test.create-failed-title'](),
        message: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setCreatingTestRun(false);
    }
  }, [
    canExecute,
    t,
    preFlightSync,
    workflowTestContext,
    workspaceId,
    docId,
    blockId,
    onTestCreated,
  ]);

  const handleStop = useCallback(() => {
    executionService.stop(blockId, workspaceId);
    // Persist log when cancelled
    handleExecutionComplete('cancelled').catch(console.error);
  }, [executionService, blockId, workspaceId, handleExecutionComplete]);

  const isIdle = state.status === 'idle';
  const isRunning = state.status === 'running' || state.status === 'queued';
  const isFinished =
    state.status === 'completed' ||
    state.status === 'cancelled' ||
    state.status === 'failed';

  // Track execution start time for elapsed timer. A queued run waits for a
  // runner pod and does not run yet: its timer starts when the backend
  // dispatches it (tsk-am-wait-in-log).
  const isExecuting = state.status === 'running';
  const executionStartRef = useRef<number | null>(null);
  if (isExecuting && executionStartRef.current === null) {
    executionStartRef.current = Date.now();
  } else if (
    (state.status === 'queued' || isIdle) &&
    executionStartRef.current !== null
  ) {
    executionStartRef.current = null;
  }
  const elapsedTime = useElapsedTimer(isExecuting, executionStartRef.current);

  // sl-3nkm.8.4: Load the workspace's plan-based workflow limits once
  // per panel mount so the timeout-warning hook can drive bar colour
  // and tooltip copy. Same source as the workspace automation settings
  // (GraphQL workspace.workflowLimits). Null while loading → hook falls back
  // to `normal`, so the pre-8.4 visual is preserved when limits are
  // unknown.
  const [workflowLimits, setWorkflowLimits] = useState<WorkflowLimits | null>(
    null
  );
  useEffect(() => {
    let cancelled = false;
    void fetchWorkflowLimits(workspaceId).then(data => {
      if (!cancelled) setWorkflowLimits(data);
    });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  // sl-3nkm.8.4: `maxStepDurationMs` is PER step. Only media steps
  // expose a step-level signal on the frontend
  // (`state.progress.media`); the backend never clears it mid-run,
  // and batch execution REUSES `stepId` across rows — only
  // `executionId` is row-unique (see
  // `AutomationBatchExecutor.executeOne`). We anchor on
  // `executionId` so the warning re-arms on every row, and drop
  // the anchor once `percent` hits 100 so a following non-media
  // step does not inherit the previous step's start time.
  // Non-media steps get no warning — preferable to a
  // confidently-wrong one driven off run-start elapsed.
  const mediaExecutionId = state.progress.media?.executionId ?? null;
  const mediaPercent = state.progress.media?.percent ?? 0;
  const stepStartRef = useRef<number | null>(null);
  const lastExecutionIdRef = useRef<string | null>(null);
  const completedExecutionIdRef = useRef<string | null>(null);
  if (!isRunning) {
    stepStartRef.current = null;
    lastExecutionIdRef.current = null;
    completedExecutionIdRef.current = null;
  } else if (
    mediaExecutionId &&
    mediaExecutionId !== lastExecutionIdRef.current
  ) {
    lastExecutionIdRef.current = mediaExecutionId;
    if (mediaPercent >= 100) {
      // First event delivered for this row is already the terminal
      // frame (fast step / throttle). Do not arm — the step is
      // done. Guards against a stale anchor outliving the row.
      stepStartRef.current = null;
      completedExecutionIdRef.current = mediaExecutionId;
    } else {
      stepStartRef.current = Date.now();
      completedExecutionIdRef.current = null;
    }
  } else if (
    mediaExecutionId &&
    mediaExecutionId === lastExecutionIdRef.current &&
    mediaPercent >= 100 &&
    completedExecutionIdRef.current !== mediaExecutionId
  ) {
    stepStartRef.current = null;
    completedExecutionIdRef.current = mediaExecutionId;
  }
  const timeoutWarning = useTimeoutWarning(
    isRunning,
    stepStartRef.current,
    workflowLimits?.maxStepDurationMs ?? null
  );

  const offlineBadge = !isOnline && (
    <span className={styles.executionOfflineBadge} aria-hidden="true">
      !
    </span>
  );

  const testContextNotice = activeAutomationTestRun && (
    <div className={runStyles.testContext}>
      <strong>
        {t['com.streamlain.streamlain.execution.test.context-active']()}
      </strong>{' '}
      {t['com.streamlain.streamlain.execution.test.context-summary']({
        rows: String(activeTestRowCount),
        docs: String(activeTestSourceDocCount),
        expiresAt: activeTestExpiresAt ?? '',
      })}
    </div>
  );

  const dialogs = (
    <>
      {/* Run Requirements Dialog - shows what's missing */}
      <RunRequirementsDialog
        open={requirementsDialogMode !== null}
        onOpenChange={open => {
          if (!open) setRequirementsDialogMode(null);
        }}
        requirements={
          requirementsDialogMode === 'test' ? testRequirements : runRequirements
        }
      />

      <TestDataDialog
        open={showTestDialog}
        loading={creatingTestRun}
        onCancel={() => setShowTestDialog(false)}
        onConfirm={() => void handleCreateTestRun()}
      />

      {chosenRows ? (
        <ChooseRowsDialog
          open={chooseRowsOpen}
          onOpenChange={setChooseRowsOpen}
          onOpenTable={chosenRows.onOpenTable}
        />
      ) : null}
    </>
  );

  if (layout === 'test')
    return (
      <div>
        {dialogs}
        <div
          className={clsx(
            styles.executionButtonFrame,
            !isOnline && runStyles.blockedFrame
          )}
          title={!isOnline ? offlineExecutionMessage : undefined}
          onClick={!isOnline ? showOfflineExecutionToast : undefined}
        >
          {offlineBadge}
          <Button
            variant="secondary"
            disabled={!canStart || creatingTestRun || isRunning}
            onClick={() => {
              if (allTestRequirementsMet) setShowTestDialog(true);
              else setRequirementsDialogMode('test');
            }}
            className={runStyles.fullWidthButton}
          >
            {creatingTestRun
              ? t['com.streamlain.streamlain.execution.test.creating']()
              : t['com.streamlain.automation.sidebar.test-with-rows']()}
          </Button>
        </div>
        {testContextNotice}
      </div>
    );

  const runBlocked = !isOnline || runLocked;
  return (
    <div className={startStyles.executionFooter}>
      {dialogs}

      {/* Start Controls (when idle or finished) */}
      {(isIdle || isFinished) && (
        <div className={runStyles.controls}>
          <div
            className={clsx(
              styles.executionButtonFrame,
              runStyles.runFrame,
              runBlocked && runStyles.blockedFrame
            )}
            title={
              !isOnline
                ? offlineExecutionMessage
                : runLocked
                  ? (runLockedHint ??
                    t['com.streamlain.streamlain.execution.disabled-hint']())
                  : undefined
            }
            onClick={
              !isOnline
                ? showOfflineExecutionToast
                : runLocked
                  ? onRunLockedClick
                  : undefined
            }
          >
            {offlineBadge}
            <Button
              variant="primary"
              disabled={!canStart || runLocked}
              onClick={() => {
                if (!allRequirementsMet) {
                  setRequirementsDialogMode('run');
                } else if (!chosenRows) {
                  handleExecute().catch(console.error);
                } else if (chosenRows.rowIds.length === 0) {
                  setChooseRowsOpen(true);
                } else {
                  handleRunRows().catch(console.error);
                }
              }}
              className={runStyles.fullWidthButton}
            >
              <span className={runStyles.buttonLabel}>
                <PlayIcon />
                {allRequirementsMet && !runLocked
                  ? t['com.streamlain.streamlain.execution.btn.run-count']({
                      count: String(runCount),
                      array: isBatchMode
                        ? t[
                            'com.streamlain.streamlain.execution.btn.run-array-suffix'
                          ]()
                        : '',
                    })
                  : t['com.streamlain.streamlain.execution.btn.run']()}
              </span>
            </Button>
          </div>
        </div>
      )}

      {/* Runtime Controls (when running) */}
      {isRunning && (
        <div className={runStyles.controls}>
          {canStop && (
            <Button
              onClick={handleStop}
              prefix={<CloseIcon />}
              className={runStyles.stopButton}
            >
              {t['com.streamlain.streamlain.execution.btn.stop']()}
            </Button>
          )}
        </div>
      )}

      <div className={startStyles.executionDetails}>
        {/* Progress (only when configured or has run history) */}
        {(isRunning || isFinished) && (
          <div className={runStyles.progress}>
            <TimeoutAwareProgressBar
              percentage={state.progress.percentage}
              status={state.status}
              phase={progressPhase}
              preFlightResult={preFlightResult}
              timeoutState={timeoutWarning.state}
              remainingMs={timeoutWarning.remainingMs}
            />
            <ProgressStats
              state={state}
              preFlightResult={preFlightResult}
              phase={progressPhase}
              preFlightExpanded={preFlightExpanded}
              onPreFlightClick={() =>
                preFlightResult && setPreFlightExpanded(v => !v)
              }
              elapsedTime={elapsedTime}
            />
            {preFlightResult && (
              <PreFlightExpandable
                result={preFlightResult}
                expanded={preFlightExpanded}
              />
            )}
          </div>
        )}

        {/* sl-g3on.9.2: Per-row retry / on-failure metadata */}
        {lastExecutionRows && lastExecutionRows.length > 0 && (
          <div className={runStyles.section}>
            <ExecutionRowList rows={lastExecutionRows} />
          </div>
        )}

        {/* sl-g3on.5.3: Backend execution warnings (i18n + structured params) */}
        {lastWarnings && lastWarnings.length > 0 && (
          <div
            className={runStyles.warnings}
            data-testid="execution-warnings-list"
          >
            {lastWarnings.map((w, idx) => (
              <WarningRow
                key={`${w.key}-${idx}`}
                warning={w}
                workflowSchema={selectedWorkflowSchema}
              />
            ))}
          </div>
        )}

        {/* Error Message (show when error exists, even if idle) */}
        {state.error &&
          (() => {
            const keyError = isAiKeyError(state.error);
            const displayError = keyError.isKeyError
              ? state.error
              : userFacingExecutionError(t, state.error);
            return (
              <div className={runStyles.errorBox} role="alert">
                {displayError}
                {keyError.isKeyError && (
                  <div>
                    <button
                      type="button"
                      className={runStyles.errorAction}
                      onClick={() =>
                        workspaceDialogService.open('setting', {
                          activeTab: 'workspace:ai',
                        })
                      }
                    >
                      {t[
                        'com.streamlain.streamlain.execution.open-ai-settings'
                      ]()}
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
      </div>
    </div>
  );
};
